'use client';

/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-case-declarations */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
  Paper,
  Avatar,
  Chip,
  Grid,
  Fade,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { TabContext, TabList, TabPanel, LoadingButton } from '@mui/lab';
import Tab from '@mui/material/Tab';
import { useNavigate } from 'react-router-dom';
// Removed direct Lucide icon imports from here
import { toast } from 'react-toastify';
import { DynamicIcon } from '../components/LucideIcon';
import { createSystemContact, getSystemContact, updateSystemContact } from '../api/contacts.api';

// Import DynamicIcon from lucide-react/dynamic

const defaultContact = [
  {
    title: 'Whatsapp',
    link: 'whatsapp://send?phone=2348012344xxxx',
    contact: '2348012344xxxx',
    imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/479px-WhatsApp.svg.png',
    category: 'transaction_report',
  },
  {
    title: 'Mobile',
    link: 'tel:2348012344xxxx',
    contact: '2348012344xxxx',
    imageUrl: 'https://cdn2.iconfinder.com/data/icons/font-awesome/1792/phone-512.png',
    category: 'contact',
  },
  {
    title: 'Email',
    link: 'mailto:support@example.com',
    contact: 'contact@email.com',
    imageUrl: 'https://static.thenounproject.com/png/223491-200.png',
    category: 'contact',
  },
];

export default function CustomersSettings() {
  const navigate = useNavigate();
  const handleNavigateBack = () => {
    navigate('/dashboard/settings');
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactName, setContactName] = useState('');
  const [selectedContactType, setSelectedContactType] = useState('');
  const [actionType, setActionType] = useState('');
  const [loading, setLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Messaging'); // State for active tab

  const handleGetSystemContact = async () => {
    setContactsLoading(true);
    try {
      const request = await getSystemContact();
      if (request.ok) {
        setContacts(request.data.data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  const getContactTitle = (contactType) => {
    const method = contactMethods.find((m) => m.id === contactType);
    return method ? method.title : 'Contact';
  };

  const handleSetupContact = (contactType) => {
    let contactExist = null;
    if (contactType === 'whatsappGroup') {
      contactExist = contacts.find((contact) => contact.link.includes('whatsapp.com'));
    } else {
      contactExist = contacts.find((contact) => contact.link.includes(contactType));
    }
    const getDefault = defaultContact.find((contact) => contact.link.includes(contactType));
    const contactTitle = getContactTitle(contactType);
    setContactName(contactTitle);
    setSelectedContactType(contactType);
    setSelectedContact(contactExist || getDefault || { title: contactTitle, contact: '', link: '' });
    setIsModalOpen(true);
    contactExist ? setActionType('update') : setActionType('create');
  };

  const getContactLink = (contactType) => {
    const method = contactMethods.find((m) => m.id === contactType);
    return method ? method.prefix : '';
  };

  const handleSaveContact = async () => {
    setLoading(true);
    const contactLink = getContactLink(selectedContactType);
    const payload = {
      ...selectedContact,
      link: contactLink + selectedContact.contact,
    };
    if (!payload.imageUrl && selectedContactType.includes('whatsapp')) {
      payload.imageUrl = 'https://i.postimg.cc/VkZxTDCc/Whats-App-svg.png';
    }
    if (actionType === 'update') {
      const request = await updateSystemContact(payload.id, payload);
      if (request.ok) {
        toast.success('Contact update successfully');
        toggleModal();
      } else {
        toast.error(request.data ? request.data.message : 'Unable to update contact please try again');
      }
    } else {
      const request = await createSystemContact(payload);
      if (request.ok) {
        toast.success('Contact created successfully');
        toggleModal();
      } else {
        toast.error(request.data ? request.data.message : 'Unable to create contact please try again');
      }
    }
    setLoading(false);
    handleGetSystemContact();
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    handleGetSystemContact();
  }, []);

  const contactMethods = [
    {
      id: 'whatsapp',
      title: 'WhatsApp Contact',
      icon: 'MessageCircle', // Use string name
      color: '#25D366',
      description: 'Direct WhatsApp messaging',
      prefix: 'whatsapp://send?phone=',
      placeholder: '+1234567890',
      category: 'Messaging',
    },
    {
      id: 'whatsappGroup',
      title: 'WhatsApp Community',
      icon: 'MessageCircle', // Use string name
      color: '#128C7E',
      description: 'WhatsApp group invitation',
      prefix: '',
      placeholder: 'https://chat.whatsapp.com/...',
      category: 'Messaging',
    },
    {
      id: 'tel',
      title: 'Mobile Contact',
      icon: 'Phone', // Use string name
      color: '#2196F3',
      description: 'Phone call support',
      prefix: 'tel:',
      placeholder: '+1234567890',
      category: 'Direct',
    },
    {
      id: 'mail',
      title: 'Email Contact',
      icon: 'Mail', // Use string name
      color: '#F44336',
      description: 'Email support',
      prefix: 'mailto:',
      placeholder: 'support@company.com',
      category: 'Direct',
    },
    {
      id: 'facebook',
      title: 'Facebook',
      icon: 'Facebook', // Use string name
      color: '#1877F2',
      description: 'Facebook business page',
      prefix: 'https://facebook.com/',
      placeholder: 'your-page-name',
      category: 'Social Media',
    },
    {
      id: 'instagram',
      title: 'Instagram',
      icon: 'Instagram', // Use string name
      color: '#E4405F',
      description: 'Instagram business profile',
      prefix: 'https://instagram.com/',
      placeholder: '@username',
      category: 'Social Media',
    },
    {
      id: 'twitter',
      title: 'X (Twitter)',
      icon: 'X', // Use string name
      color: '#000000',
      description: 'X (formerly Twitter) profile',
      prefix: 'https://twitter.com/',
      placeholder: '@username',
      category: 'Social Media',
    },
    {
      id: 'linkedin',
      title: 'LinkedIn',
      icon: 'Linkedin', // Use string name
      color: '#0A66C2',
      description: 'LinkedIn business profile',
      prefix: 'https://linkedin.com/in/',
      placeholder: 'your-profile',
      category: 'Social Media',
    },
    {
      id: 'telegram',
      title: 'Telegram',
      icon: 'MessageCircle', // Use string name
      color: '#0088CC',
      description: 'Telegram channel or chat',
      prefix: 'https://t.me/',
      placeholder: '@channel_name',
      category: 'Messaging',
    },
    {
      id: 'youtube',
      title: 'YouTube',
      icon: 'Youtube', // Use string name
      color: '#FF0000',
      description: 'YouTube business channel',
      prefix: 'https://youtube.com/',
      placeholder: '@channel_name',
      category: 'Social Media',
    },
    {
      id: 'tiktok',
      title: 'TikTok',
      icon: 'Music', // Use string name
      color: '#000000',
      description: 'TikTok business profile',
      prefix: 'https://tiktok.com/@',
      placeholder: 'username',
      category: 'Social Media',
    },
    {
      id: 'pinterest',
      title: 'Pinterest',
      icon: 'PinIcon', // Use string name for PinIcon
      color: '#BD081C',
      description: 'Pinterest business profile',
      prefix: 'https://pinterest.com/',
      placeholder: 'username',
      category: 'Social Media',
    },
    {
      id: 'snapchat',
      title: 'Snapchat',
      icon: 'Camera', // Use string name
      color: '#FFFC00',
      description: 'Snapchat business profile',
      prefix: 'https://snapchat.com/add/',
      placeholder: 'username',
      category: 'Social Media',
    },
    {
      id: 'discord',
      title: 'Discord',
      icon: 'MessageCircle', // Use string name
      color: '#5865F2',
      description: 'Discord community server',
      prefix: 'https://discord.gg/',
      placeholder: 'invite-code',
      category: 'Messaging',
    },
    {
      id: 'skype',
      title: 'Skype',
      icon: 'PhoneCall', // Use string name
      color: '#00AFF0',
      description: 'Skype business contact',
      prefix: 'skype:',
      placeholder: 'username',
      category: 'Direct',
    },
  ];

  const categories = ['Messaging', 'Direct', 'Social Media']; // Define categories for tabs

  const getContactMethod = (contactType) =>
    contactMethods.find((method) => {
      if (contactType === 'mail') return method.id === 'mail';
      return method.id === contactType;
    });

  const isContactActive = (methodId) => {
    if (methodId === 'mail') {
      return contacts.some((c) => c.link.includes('mailto'));
    }
    return contacts.some((c) => c.link.includes(methodId));
  };

  const LoadingSkeleton = () => (
    <Stack spacing={2}>
      {[1, 2, 3, 4].map((item) => (
        <Paper
          key={item}
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={3}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box>
                <Skeleton variant="text" width={150} height={24} />
                <Skeleton variant="text" width={200} height={20} />
              </Box>
            </Stack>
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );

  const EmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Avatar
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 3,
          bgcolor: 'grey.100',
          color: 'grey.400',
        }}
      >
        <DynamicIcon name="Mail" size={40} /> {/* Use DynamicIcon */}
      </Avatar>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
        No contact methods yet
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
        Start by adding your first contact method. Choose from WhatsApp, email, social media, and more to help customers
        reach you easily.
      </Typography>
      <Button
        variant="contained"
        size="large"
        sx={{
          borderRadius: 2,
          px: 4,
          py: 1.5,
          textTransform: 'none',
          fontWeight: 600,
        }}
        onClick={() => {
          const firstMethod = contactMethods.find((m) => m.category === categories[0]);
          if (firstMethod) {
            handleSetupContact(firstMethod.id);
          }
        }}
      >
        Add Your First Contact Method
      </Button>
    </Box>
  );

  return (
    <>
      {/* Modern Slide-Over Drawer */}
      <Drawer
        anchor="right"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.12)',
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'sticky', top: 0, zIndex: 10 }}>
          <Typography variant="h5" fontWeight={700}>
            Setup your {contactName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Help your customers reach you easily
          </Typography>
        </Box>

        <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Title"
              placeholder="Title"
              value={selectedContact && selectedContact.title}
              onChange={(e) => setSelectedContact({ ...selectedContact, title: e.target.value })}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label={contactName}
              placeholder={getContactMethod(selectedContactType)?.placeholder || contactName}
              value={selectedContact && selectedContact.contact}
              onChange={(e) => setSelectedContact({ ...selectedContact, contact: e.target.value })}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ p: 2.5, px: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: 'background.paper', position: 'sticky', bottom: 0, zIndex: 10 }}>
          <Button onClick={() => setIsModalOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSaveContact}
            loading={loading}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Save
          </LoadingButton>
        </Box>
      </Drawer>

      {/* Modern Layout */}
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
            <Stack direction="row" alignItems="center" spacing={3} sx={{ py: 3 }}>
              <IconButton
                onClick={handleNavigateBack}
                sx={{
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                <DynamicIcon name="ArrowLeft" size={20} /> {/* Use DynamicIcon */}
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight={700} color="text.primary">
                  Customer Care Settings
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  Configure how customers can reach you for support
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* Main Content */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
          <Grid container spacing={4}>
            {/* Contact Methods Card */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <CardHeader
                  title={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="h5" fontWeight={600}>
                        Available Contact Methods
                      </Typography>
                      {contactsLoading && <CircularProgress size={20} />}
                    </Stack>
                  }
                  subheader={
                    <Typography variant="body2" color="text.secondary">
                      Choose from various platforms to connect with your customers
                    </Typography>
                  }
                  sx={{ pb: 2 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  {contactsLoading ? (
                    <LoadingSkeleton />
                  ) : contacts.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <TabContext value={activeTab}>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={(event, newValue) => setActiveTab(newValue)} aria-label="Contact categories">
                          {categories.map((category) => (
                            <Tab key={category} label={category} value={category} />
                          ))}
                        </TabList>
                      </Box>
                      {categories.map((category) => (
                        <TabPanel key={category} value={category} sx={{ p: 0, pt: 2 }}>
                          <Stack spacing={2}>
                            {contactMethods
                              .filter((method) => method.category === category)
                              .map((method, index) => (
                                <Fade in key={method.id} timeout={300 + index * 50}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 3,
                                      border: '1px solid',
                                      borderColor: isContactActive(method.id) ? 'success.main' : 'grey.200',
                                      borderRadius: 2,
                                      transition: 'all 0.2s ease-in-out',
                                      bgcolor: isContactActive(method.id) ? 'success.50' : 'white',
                                      '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 4px 12px rgb(0 0 0 / 0.1)',
                                        transform: 'translateY(-1px)',
                                      },
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                      <Stack direction="row" alignItems="center" spacing={3}>
                                        <Avatar
                                          sx={{
                                            bgcolor: method.color,
                                            width: 56,
                                            height: 56,
                                            boxShadow: '0 4px 8px rgb(0 0 0 / 0.1)',
                                          }}
                                        >
                                          <DynamicIcon name={method.icon} size={28} color="white" />{' '}
                                          {/* Use DynamicIcon */}
                                        </Avatar>
                                        <Box>
                                          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                                            {method.title}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {method.description}
                                          </Typography>
                                          {isContactActive(method.id) && (
                                            <Chip
                                              label="Active"
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                              sx={{ mt: 1 }}
                                            />
                                          )}
                                        </Box>
                                      </Stack>
                                      <Button
                                        variant={isContactActive(method.id) ? 'outlined' : 'contained'}
                                        onClick={() => handleSetupContact(method.id)}
                                        sx={{
                                          borderRadius: 2,
                                          px: 3,
                                          py: 1,
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          boxShadow: isContactActive(method.id) ? 'none' : '0 2px 4px rgb(0 0 0 / 0.1)',
                                          '&:hover': {
                                            boxShadow: '0 4px 8px rgb(0 0 0 / 0.15)',
                                          },
                                        }}
                                      >
                                        {isContactActive(method.id) ? 'Update' : 'Setup'}
                                      </Button>
                                    </Stack>
                                  </Paper>
                                </Fade>
                              ))}
                          </Stack>
                        </TabPanel>
                      ))}
                    </TabContext>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Info Card */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: 'primary.50',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Why set up contact methods?
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        Better Customer Support
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Provide multiple ways for customers to reach you
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        Increased Trust
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show customers you're accessible and reliable
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        Quick Resolution
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resolve issues faster with direct communication
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Active Contacts Summary */}
              {!contactsLoading && contacts.length > 0 && (
                <Card
                  sx={{
                    mt: 3,
                    borderRadius: 3,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Active Contacts ({contacts.length})
                    </Typography>
                    <Stack spacing={1}>
                      {contacts.map((contact) => (
                        <Box key={contact.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'success.main',
                            }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {contact.title}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}
